import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
 

const services = [
  {
    icon: 'fa-solid fa-laptop-code',
    title: '1. Custom Enterprise Applications',
    desc: 'Build tailored, enterprise-grade software applications tailored precisely to your complex business workflows and operational goals.',
  },
  {
    icon: 'fa-solid fa-cloud',
    title: '2. Cloud-Native Application Development',
    desc: 'Architect highly available, microservice-driven cloud applications built for continuous integration, rapid deployment, and elastic scaling.',
  },
  {
    icon: 'fa-solid fa-rotate-left',
    title: '3. Legacy Application Modernization',
    desc: 'Refactor, rehost, or completely re-architect monolithic legacy software into modern, cloud-optimized, secure platforms.',
  },
  {
    icon: 'fa-solid fa-cubes',
    title: '4. SaaS Product Development',
    desc: 'Design, develop, and launch scalable multi-tenant Software-as-a-Service (SaaS) products equipped with subscription and billing features.',
  },
  {
    icon: 'fa-solid fa-plug',
    title: '5. Custom API & Microservices Engineering',
    desc: 'Engineered RESTful and GraphQL APIs that seamlessly connect internal applications, external platforms, and third-party tools.',
  },
  {
    icon: 'fa-solid fa-vial-circle-check',
    title: '6. Quality Assurance & Automated Testing',
    desc: 'Conduct rigorous manual and automated test cycles across unit, integration, and security layers to deliver bug-free software.',
  },
  {
    icon: 'fa-solid fa-sliders',
    title: '7. Application Maintenance & Evolution',
    desc: 'Ensure maximum uptime with active bug fixing, framework patching, performance monitoring, and feature expansion.',
  },
  {
    icon: 'fa-solid fa-window-restore',
    title: '8. Cross-Platform Desktop Apps',
    desc: 'Develop high-performance desktop applications for Windows, macOS, and Linux platforms using modern web and native frameworks.',
  },
  {
    icon: 'fa-solid fa-shield-cat',
    title: '9. Application Security & Compliance',
    desc: 'Embed enterprise security protocols, OWASP top-10 defense measures, and regulatory compliance standards directly into software code.',
  },
  {
    icon: 'fa-solid fa-chart-diagram',
    title: '10. Database Architecture & Design',
    desc: 'Model and optimize relational and NoSQL databases for high transaction throughput, minimal latency, and zero data loss.',
  },
];

const whyChooseUs = [
  'Full-lifecycle software engineering expertise',
  'Scalable microservices and cloud-native design',
  'Agile development sprints with clear milestones',
  'Strict adherence to security and compliance',
  'High-throughput, low-latency performance',
  'Cross-platform desktop and web coverage',
  'Robust API integration capabilities',
  'Transparent source code ownership',
  'Comprehensive automated testing suite',
  'Ongoing application support and SLA guarantees',
];

const processSteps = [
  { step: '01', title: 'Requirements & Discovery', desc: 'Define project scope, architecture blueprints, system specifications, and core user goals.' },
  { step: '02', title: 'Architecture Design', desc: 'Design scalable microservices, database models, security frameworks, and API endpoints.' },
  { step: '03', title: 'Agile Development', desc: 'Code robust software using clean architectural standards through iterative 2-week sprints.' },
  { step: '04', title: 'Automated QA & Security', desc: 'Perform multi-stage unit, security, performance, and user-acceptance testing.' },
  { step: '05', title: 'Deployment & Launch', desc: 'Automate deployment to cloud production environments using continuous integration pipelines.' },
  { step: '06', title: 'Support & Scaling', desc: 'Monitor live system metrics, continuously patch software, and rollout new features seamlessly.' },
];

const techStack = [
  { category: 'Backend & Frameworks', items: ['Node.js', 'Python', 'Java', '.NET Core', 'Go'] },
  { category: 'Frontend Tech', items: ['React.js', 'Vue.js', 'TypeScript', 'Next.js', 'Tailwind CSS'] },
  { category: 'Databases', items: ['PostgreSQL', 'MongoDB', 'Redis', 'MySQL', 'DynamoDB'] },
  { category: 'DevOps & Containers', items: ['Docker', 'Kubernetes', 'AWS', 'Azure', 'Jenkins'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const ApplicationDevelopment = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Enterprise Software
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Application <span className="gradient-text-cyan">Development</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we build high-performance, enterprise-ready software applications engineered for scale, reliability, and security across cloud, web, and desktop environments.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Build Your Software With Lidlbritain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Software Engineering by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Software Tech Stack</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Engineering Workflow</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Ready to Build Custom Software with Lidlbritain?</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform complex operational bottlenecks into seamless, automated digital software with Lidlbritain's expert application engineering team.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Start Application Development &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ApplicationDevelopment;